import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Loader2,
  Sparkles,
  Building2,
  MapPin,
  Factory,
  Copy,
  Check,
  Clock,
  ChevronRight,
  FileText,
  ArrowLeft,
  Phone,
  Mail,
  Mic,
  BarChart3,
  Globe,
  Package,
  ClipboardList,
  Download,
  Lightbulb,
  Zap,
  Save,
  FileDown
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import ReportCharts from "../components/ReportCharts";
import ReportSummary from "../components/ReportSummary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { id: "FULL_REPORT", label: "Full Report", icon: FileText, color: "bg-blue-500", desc: "Complete 10-section intelligence report" },
  { id: "CALL_PREP", label: "Call Prep", icon: Phone, color: "bg-emerald-500", desc: "Pitches, questions, objection handling" },
  { id: "EMAIL_GENERATOR", label: "Emails", icon: Mail, color: "bg-violet-500", desc: "Cold, follow-up, and proposal emails" },
  { id: "VOICE_SCRIPT", label: "Voice Script", icon: Mic, color: "bg-amber-500", desc: "Phone script with branching logic" },
  { id: "LEAD_SCORING", label: "Lead Score", icon: BarChart3, color: "bg-rose-500", desc: "Score with indicators and red flags" },
  { id: "COMPETITOR_ANALYSIS", label: "Competitors", icon: Globe, color: "bg-cyan-500", desc: "Market gaps and positioning" },
  { id: "OFFER_BUILDER", label: "Offer Builder", icon: Package, color: "bg-orange-500", desc: "Service packages, pricing, ROI" },
  { id: "CRM_UPDATE", label: "CRM Notes", icon: ClipboardList, color: "bg-slate-500", desc: "Structured CRM update notes" },
  { id: "EXPORT_PROFILE", label: "Export Profile", icon: Download, color: "bg-indigo-500", desc: "Client-ready business profile" },
  { id: "OPPORTUNITY_DETECTOR", label: "Opportunities", icon: Lightbulb, color: "bg-yellow-500", desc: "Revenue leaks and quick wins" },
];

function ReportContent({ content }) {
  return (
    <div className="prose prose-slate max-w-none prose-headings:font-heading prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h2:pb-2 prose-h2:border-b prose-h2:border-slate-200 prose-h3:text-lg prose-h3:mt-5 prose-h3:mb-2 prose-li:my-0.5 prose-p:my-2 prose-strong:text-slate-800 prose-ul:my-2">
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  );
}

function HistoryItem({ report, onSelect }) {
  const modeInfo = MODES.find(m => m.id === report.mode) || MODES[0];
  const Icon = modeInfo.icon;
  
  return (
    <button
      onClick={() => onSelect(report.report_id)}
      className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-primary/40 hover:bg-primary/5 transition-all group"
      data-testid={`history-item-${report.report_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-md ${modeInfo.color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 text-sm truncate">{report.industry}</p>
          <p className="text-xs text-slate-500">{report.mode_label || modeInfo.label}</p>
          <div className="flex items-center gap-2 mt-1">
            {report.company_name && (
              <span className="text-xs text-slate-400 truncate">{report.company_name}</span>
            )}
            {report.location && (
              <span className="text-xs text-slate-400 flex items-center gap-0.5">
                <MapPin className="w-2.5 h-2.5" />{report.location}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1">
            {new Date(report.created_at).toLocaleDateString("en-US", {
              month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
            })}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-primary transition-colors mt-2" />
      </div>
    </button>
  );
}

export default function SalesIntelligencePage() {
  const [searchParams] = useSearchParams();
  const [industry, setIndustry] = useState(searchParams.get("industry") || "");
  const [companyName, setCompanyName] = useState(searchParams.get("company") || "");
  const [location, setLocation] = useState(searchParams.get("location") || "");
  const [extraContext, setExtraContext] = useState("");
  const [selectedMode, setSelectedMode] = useState("FULL_REPORT");
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);

  // ── Optional per-request seller override (overrides org defaults) ──
  const [showSellerOverride, setShowSellerOverride] = useState(false);
  const [sellerIndustryOverride, setSellerIndustryOverride] = useState("");
  const [sellerProductOverride, setSellerProductOverride] = useState("");

  const reportRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API}/sales-intelligence/history`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.reports);
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadReport = async (reportId) => {
    setLoadingReport(true);
    try {
      const response = await fetch(`${API}/sales-intelligence/${reportId}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setReport(data);
      } else {
        toast.error("Failed to load report");
      }
    } catch (error) {
      toast.error("Failed to load report");
    } finally {
      setLoadingReport(false);
    }
  };

  const handleGenerate = async () => {
    if (!industry.trim()) {
      toast.error("Please enter an industry");
      return;
    }

    setGenerating(true);
    setReport(null);

    try {
      const response = await fetch(`${API}/sales-intelligence/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          industry: industry.trim(),
          company_name: companyName.trim(),
          location: location.trim(),
          mode: selectedMode,
          extra_context: extraContext.trim(),
          ...(sellerIndustryOverride.trim() && { seller_industry: sellerIndustryOverride.trim() }),
          ...(sellerProductOverride.trim() && { seller_services: sellerProductOverride.trim() }),
        })
      });

      if (response.ok) {
        const data = await response.json();
        setReport(data);
        toast.success("Generated successfully");
        fetchHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate");
      }
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!report?.content) return;
    try {
      await navigator.clipboard.writeText(report.content);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleSave = async () => {
    if (!report?.content) return;
    setSaving(true);
    try {
      const response = await fetch(`${API}/sales-intelligence/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          industry: report.industry,
          company_name: report.company_name,
          location: report.location,
          mode: report.mode,
          mode_label: report.mode_label,
          extra_context: report.extra_context || "",
          content: report.content,
          created_at: report.created_at
        })
      });

      if (response.ok) {
        const data = await response.json();
        setReport({ ...data, saved: true });
        toast.success("Report saved successfully");
        fetchHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save report");
      }
    } catch (error) {
      toast.error("Failed to save report");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    if (!reportRef.current || !report) return;
    setExporting(true);
    toast.info("Generating PDF...");
    try {
      const { toPng } = await import("html-to-image");
      const { default: jsPDF } = await import("jspdf");

      const el = reportRef.current;
      const imgData = await toPng(el, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        style: { padding: "32px" }
      });

      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imgData;
      });

      const pdfW = 210;
      const margin = 10;
      const contentW = pdfW - margin * 2;
      const ratio = contentW / img.width;
      const contentH = img.height * ratio;

      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const pageH = 297 - margin * 2;
      let y = margin;
      let remaining = contentH;
      let srcY = 0;

      while (remaining > 0) {
        if (y !== margin) pdf.addPage();
        const sliceH = Math.min(remaining, pageH);
        const srcSliceH = sliceH / ratio;

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = srcSliceH;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, srcY, img.width, srcSliceH, 0, 0, img.width, srcSliceH);

        pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", margin, margin, contentW, sliceH);
        srcY += srcSliceH;
        remaining -= sliceH;
        y = margin;
      }

      const slug = (report.company_name || report.industry || "report")
        .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const date = new Date(report.created_at).toISOString().slice(0, 10);
      pdf.save(`${slug}-sales-report-${date}.pdf`);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const handleBack = () => {
    setReport(null);
  };

  const activeModeInfo = MODES.find(m => m.id === selectedMode) || MODES[0];

  // Report display view
  if (report) {
    const reportMode = MODES.find(m => m.id === report.mode) || MODES[0];
    const ReportIcon = reportMode.icon;

    return (
      <div className="space-y-4 animate-fade-in-up" data-testid="sales-intelligence-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button variant="ghost" onClick={handleBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            {!report.saved && !report.report_id && (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="save-report-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Report
                  </>
                )}
              </Button>
            )}
            {(report.saved || report.report_id) && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 px-3 py-1.5 text-sm" data-testid="saved-badge">
                <Check className="w-3.5 h-3.5 mr-1.5" />
                Saved
              </Badge>
            )}
            <Button
              variant="outline"
              onClick={handleExportPdf}
              disabled={exporting}
              data-testid="download-pdf-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleCopy} data-testid="copy-report-btn">
              {copied ? (
                <Check className="w-4 h-4 mr-2 text-emerald-500" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              {copied ? "Copied" : "Copy Report"}
            </Button>
          </div>
        </div>

        <div ref={reportRef}>
          <Card data-testid="report-card">
            <CardHeader>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={`${reportMode.color} text-white border-0 text-sm`}>
                  <ReportIcon className="w-3 h-3 mr-1" />
                  {report.mode_label || reportMode.label}
                </Badge>
                <Badge className="bg-primary/10 text-primary border-0 text-sm">
                  <Factory className="w-3 h-3 mr-1" />
                  {report.industry}
                </Badge>
                {report.company_name && (
                  <Badge variant="outline" className="text-sm">
                    <Building2 className="w-3 h-3 mr-1" />
                    {report.company_name}
                  </Badge>
                )}
                {report.location && (
                  <Badge variant="outline" className="text-sm">
                    <MapPin className="w-3 h-3 mr-1" />
                    {report.location}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Generated {new Date(report.created_at).toLocaleString("en-US", {
                  month: "short", day: "numeric", year: "numeric",
                  hour: "numeric", minute: "2-digit"
                })}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingReport ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <ReportSummary
                    content={report.content}
                    mode={report.mode}
                    industry={report.industry}
                    companyName={report.company_name}
                  />
                  <ReportCharts content={report.content} mode={report.mode} />
                  <ReportContent content={report.content} />
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main form view
  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="sales-intelligence-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
          Sales Intelligence Engine
        </h1>
        <p className="text-slate-500 mt-1">
          AI-powered sales execution tools for any industry
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Mode Selector + Form */}
        <div className="lg:col-span-2 space-y-5">
          {/* Mode Selector */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                Select Mode
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {MODES.map((mode) => {
                  const Icon = mode.icon;
                  const isActive = selectedMode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      onClick={() => setSelectedMode(mode.id)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-center ${
                        isActive
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                      data-testid={`mode-${mode.id}`}
                    >
                      <div className={`w-9 h-9 rounded-lg ${isActive ? mode.color : "bg-slate-100"} flex items-center justify-center transition-colors`}>
                        <Icon className={`w-4 h-4 ${isActive ? "text-white" : "text-slate-500"}`} />
                      </div>
                      <span className={`text-xs font-medium leading-tight ${isActive ? "text-primary" : "text-slate-600"}`}>
                        {mode.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-slate-500 mt-3 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${activeModeInfo.color}`} />
                {activeModeInfo.desc}
              </p>
            </CardContent>
          </Card>

          {/* Input Form */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Input Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="industry" className="text-sm font-medium">
                  Industry *
                </Label>
                <Input
                  id="industry"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="e.g., Restaurant, HVAC, Real Estate, Dental, SaaS"
                  className="mt-1"
                  data-testid="input-industry"
                  onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company" className="text-sm font-medium">
                    Company Name
                    <span className="text-slate-400 font-normal ml-1">(optional)</span>
                  </Label>
                  <Input
                    id="company"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g., Joe's Pizza"
                    className="mt-1"
                    data-testid="input-company-name"
                  />
                </div>
                <div>
                  <Label htmlFor="location" className="text-sm font-medium">
                    Location
                    <span className="text-slate-400 font-normal ml-1">(optional)</span>
                  </Label>
                  <Input
                    id="location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Austin, TX"
                    className="mt-1"
                    data-testid="input-location"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="context" className="text-sm font-medium">
                  Additional Context
                  <span className="text-slate-400 font-normal ml-1">(optional)</span>
                </Label>
                <Textarea
                  id="context"
                  value={extraContext}
                  onChange={(e) => setExtraContext(e.target.value)}
                  placeholder="Any extra details: prospect's pain points, previous conversations, specific focus areas..."
                  rows={2}
                  className="mt-1"
                  data-testid="input-extra-context"
                />
              </div>

              {/* Optional per-request seller override */}
              <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3">
                <button
                  type="button"
                  onClick={() => setShowSellerOverride((v) => !v)}
                  className="text-xs font-medium text-slate-600 hover:text-slate-900"
                  data-testid="seller-override-toggle"
                >
                  {showSellerOverride ? "− Hide" : "+ Override"} seller context for this report
                </button>
                {showSellerOverride && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Your industry (override)</Label>
                      <Input
                        placeholder="e.g. Marketing Agency"
                        value={sellerIndustryOverride}
                        onChange={(e) => setSellerIndustryOverride(e.target.value)}
                        data-testid="seller-industry-override"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Your product / service (override)</Label>
                      <Input
                        placeholder="e.g. Local SEO + paid ads for restaurants"
                        value={sellerProductOverride}
                        onChange={(e) => setSellerProductOverride(e.target.value)}
                        data-testid="seller-product-override"
                      />
                    </div>
                    <p className="text-[11px] text-slate-500 md:col-span-2">
                      Leave blank to use your saved industry profile (Settings → Industry Profile).
                    </p>
                  </div>
                )}
              </div>

              {report && report.seller_profile_present === false && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="seller-profile-missing-banner">
                  No industry profile set — output uses neutral language. Set your industry in <a href="/settings" className="underline font-medium">Settings → Industry Profile</a> for tailored results.
                </div>
              )}

              <Button
                onClick={handleGenerate}
                disabled={generating || !industry.trim()}
                className="w-full h-11"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating {activeModeInfo.label}...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate {activeModeInfo.label}
                  </>
                )}
              </Button>

              {generating && (
                <div className="text-center py-6">
                  <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-primary/5 text-primary text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Building your {activeModeInfo.label.toLowerCase()}...
                  </div>
                  <p className="text-xs text-slate-400 mt-2">This may take 15-30 seconds</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: History */}
        <div>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <Clock className="w-5 h-5 text-slate-500" />
                Recent
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No reports yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[520px] overflow-y-auto" data-testid="report-history">
                  {history.map((r) => (
                    <HistoryItem key={r.report_id} report={r} onSelect={loadReport} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
